<!-- eslint-disable vue/require-v-for-key -->
<template>
  <div class="page-pricing">
    <div class="saas-page-head">
      <h1 class="saas-page-head__title">
        <strong>Free forever.</strong><br />Pay as you grow.
      </h1>
      <p class="saas-page-head__description">The right plan for everyone.</p>
      <div class="page-pricing__switch">
        <div class="page-pricing__version-switch">
          <HostedSelfHostedToggle v-model="version" />
        </div>
        <div class="page-pricing__yearly-switch">
          <SwitchInput v-model="yearly" :small="true" color="neutral"
            >Billed yearly</SwitchInput
          >
        </div>
      </div>

      <div class="page-pricing__plan-cards">
        <div
          v-for="(plan, planIndex) in selectedPricingPlans"
          :key="planIndex"
          class="page-pricing__plan-card"
        >
          <Badge
            v-if="plan.badge"
            class="page-pricing__card-badge"
            rounded
            :color="plan.badge.color"
            indicator
            bold
            >{{ plan.badge.label }}</Badge
          >
          <div class="page-pricing__plan-description">
            <img
              width="32"
              height="32"
              class="page-pricing__plan-icon"
              :class="{
                'page-pricing__plan-icon--fix':
                  plan.id === 'hosted-free' ||
                  plan.id === 'self-hosted-open-source',
              }"
              :src="require(`../assets/images/${plan.icon.toLowerCase()}.svg`)"
            />

            <h3 class="page-pricing__plan-name">{{ plan.name }}</h3>
            <p class="page-pricing__plan-tagline">
              {{ plan.description }}
            </p>
          </div>

          <div class="page-pricing__plan-pricing">
            <strong
              class="page-pricing__plan-pricing-price"
              :class="{
                'page-pricing__plan-pricing-price--text': plan.showPriceAsText,
              }"
            >
              {{
                yearly ? plan['usdYearlyPrice'] : plan['usdMonthlyPrice']
              }}</strong
            >
            <span
              v-if="plan.addFrequencyNextToPrice"
              class="page-pricing__plan-pricing-details"
              >per user/month<br /><template v-if="yearly"
                >billed yearly</template
              ><template v-if="!yearly">billed monthly</template></span
            >
          </div>

          <div class="page-pricing__plan-plan-pricing-sub">
            <template v-if="plan.addFrequencyNextToPrice">
              <strong>{{
                yearly ? plan['usdMonthlyPrice'] : plan['usdYearlyPrice']
              }}</strong>
              <template v-if="yearly">billed monthly</template
              ><template v-if="!yearly">billed yearly</template>
            </template>
          </div>

          <NuxtLink
            class="page-pricing__plan-button"
            :class="{
              'page-pricing__plan-button--highlight':
                plan.id === 'hosted-premium',
            }"
            :to="plan.ctaTo"
          >
            {{ plan.ctaLabel }}
          </NuxtLink>

          <p class="page-pricing__plan-include">
            <span
              v-if="
                plan.id === 'hosted-free' ||
                plan.id === 'self-hosted-open-source'
              "
              >Plan includes:</span
            >
            <span v-else
              >Everything in {{ selectedPricingPlans[planIndex - 1].name }},
              plus:</span
            >
          </p>

          <ul class="page-pricing__plan-details">
            <li v-for="usp in plan.USPs">
              <i class="iconoir-check-circle"></i><span>{{ usp }}</span>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div class="page-pricing-container">
      <h2 class="saas-striped-title">Compare plans & features</h2>

      <!-- Comparison header -->
      <div
        class="page-pricing__comparison-header page-pricing__comparison-grid page-pricing__comparison-grid--with-gap"
      >
        <div class="page-pricing__grid-col-switch">
          <div class="page-pricing__grid-col-switch-type">
            <HostedSelfHostedToggle v-model="version" :small="true" />
          </div>
          <div class="page-pricing__grid-col-switch-period">
            <SwitchInput v-model="yearly" :small="true" color="neutral"
              >Billed yearly</SwitchInput
            >
          </div>
        </div>

        <div
          v-for="(plan, planIndex) in selectedPricingPlans"
          :key="planIndex"
          class="page-pricing__comparison-card"
        >
          <span class="page-pricing__comparison-card-title">{{
            plan.name
          }}</span>
          <span class="page-pricing__comparison-card-price">
            {{ yearly ? plan['usdYearlyPrice'] : plan['usdMonthlyPrice'] }}
            <span
              v-if="
                plan.id !== 'hosted-free' &&
                plan.id !== 'self-hosted-open-source' &&
                plan.id !== 'self-hosted-enterprise'
              "
              >per user/month</span
            >
          </span>

          <NuxtLink
            class="page-pricing__plan-button page-pricing__plan-button--small"
            :class="{
              'page-pricing__plan-button--highlight':
                plan.id === 'hosted-premium',
            }"
            :to="plan.ctaTo"
          >
            {{ plan.ctaLabel }}
          </NuxtLink>
        </div>
      </div>
      <!-- Comparison header -->

      <!-- Comparison table -->
      <div class="page-pricing__comparison-section">
        <h3 class="page-pricing__comparison-section-title">Usage</h3>
        <div class="page-pricing__comparison-grid">
          <div
            v-for="(item, index) in usageItems"
            :key="`usageItem${index}`"
            class="page-pricing__comparison-row"
          >
            <div
              class="page-pricing__grid-col-feature"
              :class="{
                'page-pricing__grid-col-feature--open': item.open,
              }"
              @click="toggleItemCollapse(item)"
            >
              {{ item.name }}
            </div>
            <div v-if="item.open" class="page-pricing__grid-plan-name">
              {{ selectedPricingPlans[0].name }}
            </div>
            <div
              class="page-pricing__grid-plan-value"
              :class="{
                'page-pricing__grid-plan-value--visible': item.open,
              }"
            >
              {{ item[version][0] }}
            </div>

            <div v-if="item.open" class="page-pricing__grid-plan-name">
              {{ selectedPricingPlans[1].name }}
            </div>
            <div
              class="page-pricing__grid-plan-value"
              :class="{
                'page-pricing__grid-plan-value--visible': item.open,
              }"
            >
              {{ item[version][1] }}
            </div>
            <div v-if="item.open" class="page-pricing__grid-plan-name">
              {{ selectedPricingPlans[2].name }}
            </div>
            <div
              class="page-pricing__grid-plan-value"
              :class="{
                'page-pricing__grid-plan-value--visible': item.open,
              }"
            >
              {{ item[version][2] }}
            </div>

            <span class="page-pricing__comparison-grid-line"></span>
          </div>
        </div>
      </div>
      <div
        v-for="feature in features"
        :key="feature.name"
        class="page-pricing__comparison-section"
      >
        <h3 class="page-pricing__comparison-section-title">
          {{ feature.name }}
        </h3>
        <div class="page-pricing__comparison-grid">
          <template v-for="item in feature.items">
            <div class="page-pricing__comparison-row">
              <div
                class="page-pricing__grid-col-feature"
                :class="{
                  'page-pricing__grid-col-feature--unavailable': item.waiting,
                  'page-pricing__grid-col-feature--open': item.open,
                }"
                @click="toggleItemCollapse(item)"
              >
                {{ item.name }}
                <Badge
                  v-if="item.waiting"
                  size="small"
                  color="neutral"
                  rounded
                  bold
                  >Soon</Badge
                >
              </div>

              <div v-if="item.open" class="page-pricing__grid-plan-name">
                {{ selectedPricingPlans[0].name }}
              </div>
              <div
                class="page-pricing__grid-plan-value"
                :class="{
                  'page-pricing__grid-plan-value--visible': item.open,
                }"
              >
                <i
                  v-if="
                    !Object.prototype.hasOwnProperty.call(item, 'in') ||
                    item.in.includes(version)
                  "
                  class="page-pricing__grid-check-icon iconoir-check"
                ></i>
                <i
                  v-else
                  class="page-pricing__grid-minus-icon iconoir-minus"
                ></i>
              </div>

              <div v-if="item.open" class="page-pricing__grid-plan-name">
                {{ selectedPricingPlans[1].name }}
              </div>
              <div
                class="page-pricing__grid-plan-value"
                :class="{
                  'page-pricing__grid-plan-value--visible': item.open,
                }"
              >
                <i
                  v-if="
                    !Object.prototype.hasOwnProperty.call(item, 'in') ||
                    item.in.includes(`${version}-premium`)
                  "
                  class="page-pricing__grid-check-icon iconoir-check"
                ></i>
                <i
                  v-else
                  class="page-pricing__grid-minus-icon iconoir-minus"
                ></i>
              </div>

              <div v-if="item.open" class="page-pricing__grid-plan-name">
                {{ selectedPricingPlans[2].name }}
              </div>
              <div
                class="page-pricing__grid-plan-value"
                :class="{
                  'page-pricing__grid-plan-value--visible': item.open,
                }"
              >
                <i
                  v-if="
                    !Object.prototype.hasOwnProperty.call(item, 'in') ||
                    item.in.includes(`${version}-advanced`) ||
                    item.in.includes(`${version}-enterprise`)
                  "
                  class="page-pricing__grid-check-icon iconoir-check"
                ></i>
                <i
                  v-else
                  class="page-pricing__grid-minus-icon iconoir-minus"
                ></i>
              </div>

              <span class="page-pricing__comparison-grid-line"></span>
            </div>
          </template>
        </div>
      </div>
      <div class="page-pricing__comparison-section">
        <h3 class="page-pricing__comparison-section-title">Support</h3>
        <div class="page-pricing__comparison-grid">
          <div
            v-for="item in supportItems"
            class="page-pricing__comparison-row"
          >
            <div
              class="page-pricing__grid-col-feature"
              :class="{
                'page-pricing__grid-col-feature--open': item.open,
              }"
              @click="toggleItemCollapse(item)"
            >
              {{ item.name }}
            </div>
            <div v-if="item.open" class="page-pricing__grid-plan-name">
              {{ selectedPricingPlans[0].name }}
            </div>
            <div
              class="page-pricing__grid-plan-value"
              :class="{
                'page-pricing__grid-plan-value--visible': item.open,
              }"
              v-html="item[version][0]"
            ></div>
            <div v-if="item.open" class="page-pricing__grid-plan-name">
              {{ selectedPricingPlans[1].name }}
            </div>
            <div
              class="page-pricing__grid-plan-value"
              :class="{
                'page-pricing__grid-plan-value--visible': item.open,
              }"
              v-html="item[version][1]"
            ></div>
            <div v-if="item.open" class="page-pricing__grid-plan-name">
              {{ selectedPricingPlans[2].name }}
            </div>
            <div
              class="page-pricing__grid-plan-value"
              :class="{
                'page-pricing__grid-plan-value--visible': item.open,
              }"
              v-html="item[version][2]"
            ></div>

            <span class="page-pricing__comparison-grid-line"></span>
          </div>
        </div>
      </div>
      <!-- Comparison table -->
    </div>
    <PageFAQ :title="'Frequently asked questions'" :questions="faqs"></PageFAQ>
    <FooterJoin></FooterJoin>
  </div>
</template>

<script>
import HostedSelfHostedToggle from '@saas/components/HostedSelfHostedToggle'
import PageFAQ from '@saas/components/page/PageFAQ'
import FooterJoin from '@saas/components/footer/FooterJoin'
import FAQService from '@saas/services/faq'

export default {
  components: { HostedSelfHostedToggle, FooterJoin, PageFAQ },
  layout: 'saas',

  async asyncData({ app, route, store }) {
    const { data } = await FAQService(app.$client).fetchAll()
    return {
      version:
        route.query?.version === 'self-hosted' ? 'self-hosted' : 'hosted',
      faqs: data,
    }
  },
  data() {
    const plansData = [
      {
        id: 'hosted-free',
        name: 'Free',
        description: 'For individuals and small teams',
        usdYearlyPrice: '$0',
        usdMonthlyPrice: '$0',
        addFrequencyNextToPrice: false,
        showPriceAsText: false,
        version: 'hosted',
        usage: {
          databases: 'Unlimited',
          maxRows: '3.000',
          maxRowsUnit: 'per workspace',
          maxStorage: '2GB',
          maxStorageUnit: 'per workspace',
          rowChangeHistory: '14',
          rowChangeHistoryUnit: 'days',
        },
        supportType: 'Community, knowledge base,<br/> tutorials',
        ctaLabel: 'Get started',
        ctaTo: { name: 'signup' },
        icon: 'free-plan-icon',
        USPs: [
          'Unlimited databases',
          '3,000 rows per workspace',
          '2GB of storage per workspace',
          'Grid, Form & Gallery views',
          'Library of 65+ templates',
        ],
      },
      {
        id: 'hosted-premium',
        name: 'Premium',
        description: 'For growing teams',
        usdYearlyPrice: '$10',
        usdMonthlyPrice: '$12',
        addFrequencyNextToPrice: true,
        showPriceAsText: false,
        version: 'hosted',
        usage: {
          databases: 'Unlimited',
          maxRows: '50,000',
          maxRowsUnit: 'per workspace',
          maxStorage: '20GB',
          maxStorageUnit: 'per workspace',
          rowChangeHistory: '90 ',
          rowChangeHistoryUnit: 'days',
        },
        supportType: 'Community, knowledge base,<br/> tutorials',
        ctaLabel: 'Get started',
        ctaTo: { name: 'new-subscription' },
        icon: 'premium-plan-icon',
        badge: {
          color: 'cyan',
          label: 'Most popular',
        },
        USPs: [
          '50,000 rows per workspace',
          '20GB of storage per workspace',
          'Kanban, Survey & Calendar views',
          'XML & JSON export',
          'Row comments & coloring',
        ],
      },
      {
        id: 'hosted-advanced',
        name: 'Advanced',
        description: 'For scaling businesses',
        usdYearlyPrice: '$20',
        usdMonthlyPrice: '$22',
        addFrequencyNextToPrice: true,
        showPriceAsText: false,
        version: 'hosted',
        usage: {
          databases: 'Unlimited',
          maxRows: '250,000',
          maxRowsUnit: 'per workspace',
          maxStorage: '100GB',
          maxStorageUnit: 'per workspace',
          rowChangeHistory: '180',
          rowChangeHistoryUnit: 'days',
        },
        supportType: 'Within 1 business day<br/> (direct and priority)',
        ctaLabel: 'Try now for free',
        ctaTo: { name: 'dashboard' },
        icon: 'advanced-plan-icon',
        badge: {
          color: 'magenta',
          label: 'Free trial',
        },
        USPs: [
          '250,000 rows per workspace',
          '100GB of storage per workspace',
          'Role based permissions',
          'Free read/comment users',
          'Audit logs',
        ],
      },
      {
        id: 'self-hosted-open-source',
        name: 'Open source',
        usp: 'Access to the latest features',
        description: 'For individuals and small teams.',
        usdYearlyPrice: 'Always free',
        usdMonthlyPrice: 'Always free',
        addFrequencyNextToPrice: false,
        showPriceAsText: true,
        version: 'self-hosted',
        usage: {
          databases: 'Unlimited',
          maxRows: 'Unlimited',
          maxRowsUnit: '',
          maxStorage: 'Unlimited',
          maxStorageUnit: '',
          rowChangeHistory: 'Unlimited',
          rowChangeHistoryUnit: '',
        },
        supportType: 'Community, knowledge base,<br/> tutorials',
        ctaLabel: 'Instructions',
        ctaTo: {
          name: 'dev-docs',
          params: {
            path: 'installation/install-with-docker',
          },
        },
        icon: 'free-plan-icon',
        USPs: [
          'Unlimited databases',
          'Unlimited rows',
          'Unlimited storage',
          'Grid, Form & Gallery views',
          'Library of 65+ templates',
        ],
      },
      {
        id: 'self-hosted-premium',
        name: 'Premium',
        description: 'For growing teams',
        usdYearlyPrice: '$10',
        usdMonthlyPrice: '$12',
        addFrequencyNextToPrice: true,
        showPriceAsText: false,
        version: 'self-hosted',
        usage: {
          databases: 'Unlimited',
          maxRows: 'Unlimited',
          maxRowsUnit: '',
          maxStorage: 'Unlimited',
          maxStorageUnit: '',
          rowChangeHistory: 'Unlimited',
          rowChangeHistoryUnit: '',
        },
        supportType: 'Community, knowledge base,<br/> tutorials',
        ctaLabel: 'Choose premium',
        ctaTo: { name: 'new-subscription' },
        icon: 'premium-plan-icon',
        USPs: [
          'Kanban, Survey & Calendar views',
          'XML & JSON export',
          'Row comments',
          'Row coloring',
          'Public logo removal',
        ],
      },
      {
        id: 'self-hosted-enterprise',
        name: 'Enterprise',
        description: 'For scaling businesses',
        usdYearlyPrice: 'On request',
        usdMonthlyPrice: 'On request',
        addFrequencyNextToPrice: false,
        showPriceAsText: true,
        version: 'self-hosted',
        usage: {
          databases: 'Unlimited',
          maxRows: 'Unlimited',
          maxRowsUnit: '',
          maxStorage: 'Unlimited',
          maxStorageUnit: '',
          rowChangeHistory: 'Unlimited',
          rowChangeHistoryUnit: '',
        },
        supportType: 'Within 1 business day<br/> (direct and priority)',
        ctaLabel: 'Contact sales',
        ctaTo: { name: 'contact-sales' },
        icon: 'advanced-plan-icon',
        USPs: [
          'Role based permissions',
          'Free read/comment users',
          'Single sign-on',
          'Audit logs',
          'Co-branding',
        ],
      },
    ]
    return {
      plans: plansData,
      yearly: true,
      usageItems: [
        {
          name: 'Databases',
          open: false,
          hosted: [
            plansData[0].usage.databases,
            plansData[1].usage.databases,
            plansData[2].usage.databases,
          ],
          'self-hosted': [
            plansData[3].usage.databases,
            plansData[4].usage.databases,
            plansData[5].usage.databases,
          ],
        },
        {
          name: 'Rows',
          open: false,
          hosted: [
            `${plansData[0].usage.maxRows} ${plansData[0].usage.maxRowsUnit}`,
            `${plansData[1].usage.maxRows} ${plansData[1].usage.maxRowsUnit}`,
            `${plansData[2].usage.maxRows} ${plansData[2].usage.maxRowsUnit}`,
          ],
          'self-hosted': [
            `${plansData[3].usage.maxRows} ${plansData[3].usage.maxRowsUnit}`,
            `${plansData[4].usage.maxRows} ${plansData[4].usage.maxRowsUnit}`,
            `${plansData[5].usage.maxRows} ${plansData[5].usage.maxRowsUnit}`,
          ],
        },
        {
          name: 'Storage',
          open: false,
          hosted: [
            `${plansData[0].usage.maxStorage} ${plansData[0].usage.maxStorageUnit}`,
            `${plansData[1].usage.maxStorage} ${plansData[1].usage.maxStorageUnit}`,
            `${plansData[2].usage.maxStorage} ${plansData[2].usage.maxStorageUnit}`,
          ],
          'self-hosted': [
            `${plansData[3].usage.maxStorage} ${plansData[3].usage.maxStorageUnit}`,
            `${plansData[4].usage.maxStorage} ${plansData[4].usage.maxStorageUnit}`,
            `${plansData[5].usage.maxStorage} ${plansData[5].usage.maxStorageUnit}`,
          ],
        },
        {
          name: 'Row change history',
          open: false,
          hosted: [
            `${plansData[0].usage.rowChangeHistory} ${plansData[0].usage.rowChangeHistoryUnit}`,
            `${plansData[1].usage.rowChangeHistory} ${plansData[1].usage.rowChangeHistoryUnit}`,
            `${plansData[2].usage.rowChangeHistory} ${plansData[2].usage.rowChangeHistoryUnit}`,
          ],
          'self-hosted': [
            `${plansData[3].usage.rowChangeHistory} ${plansData[3].usage.rowChangeHistoryUnit}`,
            `${plansData[4].usage.rowChangeHistory} ${plansData[4].usage.rowChangeHistoryUnit}`,
            `${plansData[5].usage.rowChangeHistory} ${plansData[5].usage.rowChangeHistoryUnit}`,
          ],
        },
      ],
      supportItems: [
        {
          name: 'User support',
          open: false,
          hosted: [
            plansData[0].supportType,
            plansData[1].supportType,
            plansData[2].supportType,
          ],
          'self-hosted': [
            plansData[3].supportType,
            plansData[4].supportType,
            plansData[5].supportType,
          ],
        },
      ],
      features: [
        {
          name: 'Features',
          items: [
            {
              name: 'Filtering',
              open: false,
            },
            {
              name: 'Sorting',
              open: false,
            },
            {
              name: 'Search',
              open: false,
            },
            {
              name: 'Templates',
              open: false,
            },
            {
              name: 'Public REST API',
              open: false,
            },
            {
              name: 'API token permissions',
              open: false,
            },
            {
              name: 'CSV, XML and JSON import',
              open: false,
            },
            {
              name: 'CSV export',
              open: false,
            },
            {
              name: 'Trash',
              open: false,
            },
            {
              name: 'Undo/redo',
              open: false,
            },
            {
              name: 'Snapshots',
              open: false,
            },
            {
              name: 'Webhooks',
              open: false,
            },
            {
              name: 'Public view sharing',
              open: false,
            },
            {
              name: 'Footer summary',
              open: false,
            },
            {
              name: 'Inviting others',
              open: false,
            },
            {
              name: 'Group by',
              open: false,
            },
            {
              name: 'Application builder',
              open: false,
            },
            {
              name: 'Custom dashboard',
              open: false,
            },
            {
              name: 'XML and JSON export',
              in: [
                'hosted-premium',
                'hosted-advanced',
                'self-hosted-premium',
                'self-hosted-enterprise',
              ],
              open: false,
            },
            {
              name: 'Row comments',
              in: [
                'hosted-premium',
                'hosted-advanced',
                'self-hosted-premium',
                'self-hosted-enterprise',
              ],
              open: false,
            },
            {
              name: 'Row coloring',
              in: [
                'hosted-premium',
                'hosted-advanced',
                'self-hosted-premium',
                'self-hosted-enterprise',
              ],
              open: false,
            },
            {
              name: 'Public logo removal',
              in: [
                'hosted-premium',
                'hosted-advanced',
                'self-hosted-premium',
                'self-hosted-enterprise',
              ],
              open: false,
            },
            {
              name: 'Role based permissions',
              in: ['hosted-advanced', 'self-hosted-enterprise'],
              open: false,
            },
            {
              name: 'Free read/comment users',
              in: ['hosted-advanced', 'self-hosted-enterprise'],
              open: false,
            },
            {
              name: 'Chart widget',
              in: [
                'hosted-premium',
                'hosted-advanced',
                // @TODO change this once 1.33 is released.
                // 'self-hosted-premium',
                'self-hosted-enterprise',
              ],
              open: false,
            },
            {
              name: '2FA',
              waiting: true,
              open: false,
            },
            {
              name: 'Search and replace',
              waiting: true,
              open: false,
            },
          ],
        },
        {
          name: 'Views',
          items: [
            {
              name: 'Grid',
              open: false,
            },
            {
              name: 'Form',
              open: false,
            },
            {
              name: 'Gallery',
              open: false,
            },
            {
              name: 'Kanban',
              open: false,
              in: [
                'hosted-premium',
                'hosted-advanced',
                'self-hosted-premium',
                'self-hosted-enterprise',
              ],
            },
            {
              name: 'Survey',
              open: false,
              in: [
                'hosted-premium',
                'hosted-advanced',
                'self-hosted-premium',
                'self-hosted-enterprise',
              ],
            },
            {
              name: 'Calendar',
              open: false,
              in: [
                'hosted-premium',
                'hosted-advanced',
                'self-hosted-premium',
                'self-hosted-enterprise',
              ],
            },
            {
              name: 'Timeline',
              waiting: false,
              open: false,
              in: [
                'hosted-premium',
                'hosted-advanced',
                'self-hosted-premium',
                'self-hosted-enterprise',
              ],
            },
            {
              name: 'Personal views',
              waiting: false,
              open: false,
              in: [
                'hosted-premium',
                'hosted-advanced',
                'self-hosted-premium',
                'self-hosted-enterprise',
              ],
            },
          ],
        },
        {
          name: 'Fields',
          items: [
            {
              name: 'Single line text',
              open: false,
            },
            {
              name: 'Long text',
              open: false,
            },
            {
              name: 'Link to table',
              open: false,
            },
            {
              name: 'Number',
              open: false,
            },
            {
              name: 'Boolean',
              open: false,
            },
            {
              name: 'URL',
              open: false,
            },
            {
              name: 'Email',
              open: false,
            },
            {
              name: 'Phone',
              open: false,
            },
            {
              name: 'File',
              open: false,
            },
            {
              name: 'Multiple select',
              open: false,
            },
            {
              name: 'Formula',
              open: false,
            },
            {
              name: 'Created',
              open: false,
            },
            {
              name: 'Last modified',
              open: false,
            },
            {
              name: 'Lookup',
              open: false,
            },
            {
              name: 'Rating',
              open: false,
            },
            {
              name: 'Collaborators',
              open: false,
            },
            {
              name: 'Rollup',
              open: false,
            },
            {
              name: 'Count',
              open: false,
            },
            {
              name: 'Created by',
              open: false,
            },
            {
              name: 'Last modified by',
              open: false,
            },
            {
              name: 'UUID',
              open: false,
            },
            {
              name: 'Duration',
              open: false,
            },
            {
              name: 'Rich text',
              open: false,
              waiting: false,
            },
            {
              name: 'AI prompt',
              open: false,
              waiting: false,
              in: [
                'hosted-premium',
                'hosted-advanced',
                'self-hosted-premium',
                'self-hosted-enterprise',
              ],
            },
            {
              name: 'AI formula generator',
              open: false,
              waiting: false,
              in: [
                'hosted-premium',
                'hosted-advanced',
                'self-hosted-premium',
                'self-hosted-enterprise',
              ],
            },
            {
              name: 'Barcode',
              open: false,
              waiting: true,
            },
            {
              name: 'Percent',
              open: false,
              waiting: true,
            },
          ],
        },
        {
          name: 'Data sync',
          items: [
            {
              name: 'iCal',
              open: false,
            },
            {
              name: 'PostgreSQL table',
              open: false,
            },
            {
              name: 'Baserow table',
              open: false,
              in: ['hosted-advanced', 'self-hosted-enterprise'],
            },
            {
              name: 'Jira issues',
              open: false,
              in: ['hosted-advanced', 'self-hosted-enterprise'],
            },
            {
              name: 'GitLab issues',
              open: false,
              in: ['hosted-advanced', 'self-hosted-enterprise'],
            },
            {
              name: 'GitHub issues',
              open: false,
              in: ['hosted-advanced', 'self-hosted-enterprise'],
            },
            {
              name: 'HubSpot customers',
              open: false,
              waiting: false,
              in: ['hosted-advanced', 'self-hosted-enterprise'],
            },
            {
              name: 'Salesforce customers',
              open: false,
              waiting: true,
              in: ['hosted-advanced', 'self-hosted-enterprise'],
            },
            {
              name: 'Salesforce customers',
              open: false,
              waiting: true,
              in: ['hosted-advanced', 'self-hosted-enterprise'],
            },
            {
              name: 'Pipedrive customers',
              open: false,
              waiting: true,
              in: ['hosted-advanced', 'self-hosted-enterprise'],
            },
            {
              name: 'API',
              open: false,
              waiting: true,
              in: ['hosted-advanced', 'self-hosted-enterprise'],
            },
          ],
        },
        {
          name: 'Admin',
          items: [
            {
              name: 'Instance-wide admin panel',
              open: false,
              in: ['self-hosted-enterprise'],
            },
            {
              name: 'Single sign-on (SSO)',
              open: false,
              in: ['self-hosted-enterprise'],
            },
            {
              name: 'Payment by invoice',
              open: false,
              in: ['self-hosted-enterprise'],
            },
            {
              name: 'Audit logs',
              open: false,
              in: ['hosted-advanced', 'self-hosted-enterprise'],
            },
            {
              name: 'Co-branding',
              open: false,
              in: ['self-hosted-enterprise'],
            },
            {
              name: 'Signup rules',
              open: false,
              waiting: true,
              in: ['self-hosted-enterprise'],
            },
          ],
        },
      ],
    }
  },
  head() {
    return {
      title: 'Pricing',
      meta: [
        {
          name: 'description',
          content:
            'Pricing of Baserow for the hosted and self hosted version. Including a feature breakdown of these features.',
        },
      ],
    }
  },
  computed: {
    selectedPricingPlans() {
      return this.plans.filter((plan) => plan.version === this.version)
    },
  },
  methods: {
    visibleItems(plan, items) {
      return items.filter((item) => {
        return (
          !Object.prototype.hasOwnProperty.call(item, 'in') ||
          item.in.includes(plan.id)
        )
      })
    },
    toggleItemCollapse(item) {
      item.open = !item.open
    },
  },
}
</script>
